import { get, keyBy } from "lodash"
import { FACTOR_SENSITIVITY_LOOKBACK_DAYS } from "helpers/factorSensitivity"
import { sanitizePositionSet } from "../helpers"
import { groupFactorsByCategory, FACTOR_SENSITIVITY_NON_AGGREGABLE_KEYS } from "./helpers/factor-utils"

function getFactorSensitivityQuery(factorSensitivityType, days) {
  return `factorSensitivity (
    type: ${factorSensitivityType}
    ${days ? `days: ${days}` : ""}
    horizon: $horizon
  ) {
    factors {
      id
      name
      category
      sensitivity
    }
  }`
}

const getQuery = (isOpPro, shouldUseIntradayQuery) => {
  const innerQuery = `
    ${!isOpPro ? `predictedFactorSensitivity: ${getFactorSensitivityQuery("PREDICTED")}` : ""}
    historicalFactorSensitivity: ${getFactorSensitivityQuery("HISTORICAL", FACTOR_SENSITIVITY_LOOKBACK_DAYS)}
  `

  if (shouldUseIntradayQuery) {
    return `
      query (
        $modelId: String!,
        $positionSet: PositionSetInput!,
        $on: Date!,
        $batchId: Id,
        $horizon: FactorSensitivityTimeHorizon!
      ) {
        model(id: $modelId) {
          simulation:intradaySimulationBatch (
            positionSet: $positionSet
            on: $on
            batchId: $batchId
          ) {
            ${innerQuery}
          }
        }
      }
    `
  }

  return `
    query (
      $modelId: String!,
      $positionSet: PositionSetInput!,
      $base: [PositionSetInput],
      $date: Date!,
      $horizon: FactorSensitivityTimeHorizon!
    ) {
      model(id: $modelId) {
        simulation (
          positionSet: $positionSet
          base: $base
          from: $date
          to: $date
          interval: END_DATE
        ) {
          ${innerQuery}
        }
      }
    }
  `
}

function getGroupedFactors(factors) {
  return groupFactorsByCategory(factors, FACTOR_SENSITIVITY_NON_AGGREGABLE_KEYS)
}

function getFactorData(historicalFactorSensitivity = [], predictedFactorSensitivity = [], prefix) {
  const predictedFactorSensitivityMap = keyBy(predictedFactorSensitivity, "id")
  return historicalFactorSensitivity.reduce((acc, factor) => {
    const historicalFactorSensitivity = factor.sensitivity
    const predictedFactorSensitivity = predictedFactorSensitivityMap[factor.id]?.sensitivity
    return {
      ...acc,
      [factor.id]: {
        id: factor.id,
        label: factor.name,
        category: factor.category,
        values: {
          [prefix]: {
            historical: historicalFactorSensitivity ? historicalFactorSensitivity * 100 : null,
            predicted: predictedFactorSensitivity ? predictedFactorSensitivity * 100 : null,
          },
        },
      },
    }
  }, {})
}

export function createFactorSensitivityQuery({ prefix, modelId, positionSet, base, to, selectedCategory, isOpPro, on, batchId, shouldUseIntradayQuery }) {
  const sanitizedPositionSet = sanitizePositionSet(positionSet)

  return {
    prefix,
    key: ["sensitivity", "factors", prefix, modelId, positionSet, base, to, selectedCategory, shouldUseIntradayQuery, on, batchId],
    query: getQuery(isOpPro, shouldUseIntradayQuery),
    variables: {
      modelId,
      positionSet: sanitizedPositionSet,
      on,
      batchId,
      base,
      date: to,
      horizon: "MONTHLY",
      category: selectedCategory,
    },
    select: (result) => {
      const historicalFactorSensitivity = get(result, "data.model.simulation.historicalFactorSensitivity.0.factors")
      const predictedFactorSensitivity = get(result, "data.model.simulation.predictedFactorSensitivity.0.factors")
      const children = getFactorData(historicalFactorSensitivity, predictedFactorSensitivity, prefix)
      const factors = {
        label: "Factors",
        values: {},
        children,
        pinnedRowOrder: 2,
      }

      return {
        children: {
          Specific: {},
          Factors: factors,
          ...getGroupedFactors(children),
        },
      }
    },
  }
}
